import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { PageHeader, Button, Modal, Input, Select, EmptyState, Money } from '../components/ui';
import { FormError } from '../components/forms/common';
import { useStatFilter } from '../hooks/useStatFilter';
import { StatTile, FilterNote } from '../components/charts/StatTile';
import { formatMoney } from '../lib/viz';
import { arrearsApi, STEP_SHORT, type ArrearsRow, type LadderRun } from '../lib/arrears';
import { ArrearsList } from '../components/arrears/ArrearsList';
import { ArrearsDrawer } from '../components/arrears/ArrearsDrawer';


/**
 * Arrears (prototype §27). Every tenancy with rent past due, worst first, climbing a ladder of
 * reminders at 7, 14, 21 and 31 days. Each letter is written from the standard template, sent
 * through the outbox and recorded against the tenancy; a promise to pay pauses the ladder; a
 * receipt that clears the arrears closes the loop on its own.
 */
export default function ArrearsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { tenancyId } = useParams<{ tenancyId?: string }>();
  const [params, setParams] = useSearchParams();
  const propertyId = params.get('propertyId') ?? '';
  // The tiles are filters (prototype §38): ?filter=overMonth (31+ days — the Dashboard's drill) | due.
  const tf = useStatFilter<'overMonth' | 'due'>();
  const minDays = tf.is('overMonth') ? '31' : params.get('minDays') ?? '';
  const step = params.get('step') ?? '';
  const due = tf.is('due') || params.get('due') === '1';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => { const t = setTimeout(() => setQ(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }, [toast]);
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    setParams(next, { replace: true });
  };

  const summary = useQuery({ queryKey: ['/arrears/summary', propertyId], queryFn: () => arrearsApi.summary({ propertyId: propertyId || undefined }) });
  const all = useQuery({ queryKey: ['/arrears/summary', ''], queryFn: () => arrearsApi.summary({}) });
  const list = useQuery({
    queryKey: ['/arrears', propertyId, minDays, step, due, q],
    queryFn: () => arrearsApi.list({ propertyId: propertyId || undefined, minDays: minDays ? Number(minDays) : undefined, step: step ? Number(step) : undefined, due, q: q || undefined }),
  });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['/arrears'] }); void qc.invalidateQueries({ queryKey: ['/arrears/summary'] }); void qc.invalidateQueries({ queryKey: ['/arrears/detail'] }); };
  const s = summary.data;
  const openRow = (r: ArrearsRow) => navigate({ pathname: `/app/arrears/${r.tenancyId}`, search: params.toString() });
  const close = () => navigate({ pathname: '/app/arrears', search: params.toString() });
  const filtered = !!(propertyId || minDays || step || due || q);

  return (
    <div className="space-y-2.5">
      <PageHeader title="Arrears"
        menu={[{ label: `Run ladder${s?.dueNow ? ` (${s.dueNow})` : ''}`, icon: 'Send', can: 'arrears.remind', disabled: !s?.dueNow, onClick: () => setRunning(true) }, { label: 'Export CSV', export: { path: '/arrears', page: 'arrears', query: { propertyId: propertyId || undefined, minDays: minDays || undefined, step: step || undefined, due: due ?? undefined, q: q || undefined } } }]} />

      <div className="tile-row">
        <StatTile compact label="In arrears" value={s?.count ?? '—'} hint="show everyone" onClick={() => { set({ due: null, step: null, minDays: null, filter: null }); }} />
        <StatTile compact label="Over a month" value={s?.overMonth ?? '—'} hint={<Money minor={s?.owedMinor ?? 0} currency={s?.currency} />} status={s?.overMonth ? 'critical' : undefined} {...tf.tile('overMonth')} />
        <StatTile compact label="Due a reminder today" value={s?.dueNow ?? '—'} hint={s?.dueNow ? 'ready to send' : 'nothing to send'} status={s?.dueNow ? 'warning' : undefined} {...tf.tile('due')} />
        <StatTile compact label="Promises to pay" value={s?.promisesOutstanding ?? '—'} hint={s?.promisesBroken ? `${s.promisesBroken} missed` : 'ladder paused'} />
      </div>
      {tf.active && <FilterNote label={tf.active === 'overMonth' ? 'tenancies more than a month behind' : 'tenancies due a reminder today'} count={list.data?.length} onClear={tf.clear} />}

      {!!all.data?.byProperty.length && all.data.byProperty.length > 1 && (
        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5">
          <PropChip on={!propertyId} label="Every property" n={all.data.count} owed={all.data.owedMinor} onClick={() => set({ propertyId: null })} />
          {all.data.byProperty.map((p) => <PropChip key={p.propertyId} on={propertyId === p.propertyId} label={p.propertyName} n={p.count} owed={p.owedMinor} onClick={() => set({ propertyId: propertyId === p.propertyId ? null : p.propertyId })} />)}
        </div>
      )}

      <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tenant, flat, reference…" className="col-span-2 min-w-0 sm:w-56" />
        <Select value={minDays} onChange={(e) => set({ minDays: e.target.value || null, filter: tf.is('overMonth') ? null : params.get('filter') })} className="min-w-0 sm:w-40" aria-label="Days late">
          <option value="">Any days late</option>
          {[7, 14, 21, 31, 60].map((d) => <option key={d} value={d}>{d}+ days</option>)}
        </Select>
        <Select value={step} onChange={(e) => set({ step: e.target.value || null })} className="min-w-0 sm:w-44" aria-label="Ladder step">
          <option value="">Any step</option>
          {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{STEP_SHORT[n]}</option>)}
        </Select>
        <label className="col-span-2 flex items-center gap-1.5 px-1 text-[12.5px] text-ink-600"><input type="checkbox" className="accent-bronze-500" checked={due} onChange={(e) => set({ due: e.target.checked ? '1' : null, filter: !e.target.checked && tf.is('due') ? null : params.get('filter') })} /> Due today</label>
        <div className="hidden flex-1 sm:block" />
        {(list.isFetching || summary.isFetching) && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-bronze-400" />}
      </div>

      {list.isLoading ? (
        <div className="surface space-y-2.5 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-ink-100" />)}</div>
      ) : list.error ? (
        <EmptyState icon="TriangleAlert" title="Could not load the arrears" body={(list.error as Error).message} />
      ) : !list.data?.length ? (
        filtered
          ? <EmptyState icon="Filter" title="Nothing matches" body="No tenancy in arrears matches those filters." action={<Button variant="secondary" onClick={() => { setSearch(''); setParams(new URLSearchParams(), { replace: true }); }}>Show everything</Button>} />
          : <EmptyState icon="CircleCheck" title="Nobody is behind" body="Every instalment past its due date has been paid. Rent counts as paid once its receipt is recorded — or its bank line allocated in Banking." />
      ) : (
        <ArrearsList rows={list.data} activeId={tenancyId} onOpen={openRow} />
      )}

      {tenancyId && <ArrearsDrawer tenancyId={tenancyId} onClose={close} onChanged={(msg) => { refresh(); setToast(msg); }} />}
      {running && <RunLadderDialog propertyId={propertyId || undefined} onClose={() => setRunning(false)} onDone={(r) => { refresh(); setToast(`${r.counts.sent} reminder${r.counts.sent === 1 ? '' : 's'} sent, ${r.counts.skipped} skipped${r.counts.failed ? `, ${r.counts.failed} failed` : ''}`); }} />}
      {toast && <div className="fixed inset-x-4 bottom-4 z-[60] mx-auto max-w-md rounded-lg bg-ink-900 px-4 py-3 text-[13px] text-ink-50 shadow-pop" role="status">{toast}</div>}
    </div>
  );
}


function PropChip({ on, label, n, owed, onClick }: { on: boolean; label: string; n: number; owed: number; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border px-2.5 text-[12px] transition ${on ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50'}`}>
      {label} <span className={on ? 'text-bronze-600' : 'text-ink-400'}>· {n} · {formatMoney(owed)}</span>
    </button>
  );
}

/** Confirm first: list exactly what will be sent (a dry run), then send it. */
function RunLadderDialog({ propertyId, onClose, onDone }: { propertyId?: string; onClose: () => void; onDone: (r: LadderRun) => void }) {
  const plan = useQuery({ queryKey: ['/arrears/run/plan', propertyId], queryFn: () => arrearsApi.run({ dryRun: true, propertyId }), staleTime: 0 });
  const [result, setResult] = useState<LadderRun | null>(null);
  const run = useMutation({ mutationFn: () => arrearsApi.run({ propertyId }), onSuccess: (r) => { setResult(r); onDone(r); } });
  const p = result ?? plan.data;
  const paused = p?.skipped.filter((x) => x.reason === 'PROMISE') ?? [];
  return (
    <Modal open onClose={onClose} title={result ? 'Ladder run' : 'Run the ladder'} size="lg"
      footer={result ? <Button onClick={onClose}>Done</Button> : <>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button icon="Send" disabled={!plan.data?.counts.due || run.isPending} onClick={() => run.mutate()}>{run.isPending ? 'Sending…' : `Send ${plan.data?.counts.due ?? 0} reminder${plan.data?.counts.due === 1 ? '' : 's'}`}</Button>
      </>}>
      {plan.isLoading ? <div className="h-40 animate-pulse rounded-lg bg-ink-100" /> : plan.error ? <FormError error={plan.error} /> : p && (
        <div className="space-y-3 text-[12.5px]">
          <p className="text-ink-500">{result
            ? `${result.counts.sent} sent (${result.counts.letters} letter${result.counts.letters === 1 ? '' : 's'}), ${result.counts.skipped} skipped, ${result.counts.failed} failed.`
            : `Each tenancy below gets the letter for the step it has reached, by email, now. Nothing already sent goes twice; tenancies with a promise to pay are left alone.`}</p>
          <RunList title={result ? 'Sent' : 'Will be sent'} rows={p.sent.map((x) => ({ key: x.tenancyId, a: `${x.unitReference ?? x.reference} · ${x.propertyName}`, b: x.tenants ?? '', c: `${x.label} · ${formatMoney(x.owedMinor)} · ${x.daysOverdue}d`, d: x.letters.map((l) => `${l.to ?? 'no email'} (${l.status.toLowerCase()})`).join(', ') }))} />
          {!!result?.failed.length && <RunList title="Failed" rows={result.failed.map((x) => ({ key: x.tenancyId, a: `${x.unitReference ?? x.reference} · ${x.propertyName}`, b: x.tenants ?? '', c: x.label ?? '', d: x.error }))} />}
          {!!paused.length && <RunList title="Paused — promise to pay" rows={paused.map((x) => ({ key: x.tenancyId, a: `${x.unitReference ?? x.reference} · ${x.propertyName}`, b: x.tenants ?? '', c: x.label ?? '', d: x.detail }))} />}
          <FormError error={run.error} />
        </div>
      )}
    </Modal>
  );
}
function RunList({ title, rows }: { title: string; rows: Array<{ key: string; a: string; b: string; c: string; d: string }> }) {
  if (!rows.length) return null;
  return (
    <div>
      <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-500">{title} · {rows.length}</h4>
      <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
        {rows.map((r) => (
          <li key={r.key} className="px-3.5 py-2">
            <div className="flex flex-wrap justify-between gap-x-3"><b className="font-medium text-ink-900">{r.a}</b><span className="text-ink-600">{r.c}</span></div>
            <div className="truncate text-[11.5px] text-ink-400">{r.b}{r.d ? ` · ${r.d}` : ''}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
